import React, { FC, useEffect, useMemo, useState } from 'react';
import { useErrorHandler } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useBrandGroupConfig } from '../../hooks/useBrandGroupConfig';
import { RegistrationFlowConfiguration } from '../../hooks/useRegistrationFlowConfiguration';
import { useAuthenticationContext } from '../../providers/AuthenticationProvider';
import { registrationCompletionService } from '../../services/registrationCompletionService';
import { OptInState, RegistrationFlowFormValues } from '../../types/registrationFlows';
import { EmailVerification } from './EmailVerification';
import { RegistrationFlowForm } from './RegistrationFlowForm';
import { RootState } from '../../redux/store';
import { useAddressService } from '../../hooks/useAddressService';
import { Stack } from '@mediahuis/chameleon-react';
import { useInitialFormValues } from '../../hooks/useInitialFormValues';
import Loader from '../WrapperComponents/Loader';
import { useTracking, getEventCategory } from '../../hooks/useTracking';
import { setContext } from '../../redux/reducers/contextReducer';
import { EventActionValues, EventLabelValues, VIEWS } from '../../types/tracking';
import { useAppDispatch } from '../../redux/hooks';
import { useQueryParams } from '../../hooks/useQueryParams';
import { useRegistrationFlowOrchestration } from '../../hooks/useRegistrationFlowOrchestration';
import { CountryConfig } from '../../types/apiConfig';
import { useGetCountryConfig } from '../../hooks/useCountryConfig';
import { useProgressiveRegistrationRedirect } from '../../hooks/useProgressiveRegistrationRedirect';

type RegistrationFlowProps = {
  registrationFlowConfig: RegistrationFlowConfiguration;
};

export const RegistrationFlow: FC<RegistrationFlowProps> = ({ registrationFlowConfig }) => {
  const [showEmailVerification, setShowEmailVerification] = useState<boolean>(false);
  const { t } = useTranslation();
  const handleError = useErrorHandler();
  const { user, getAccessToken } = useAuthenticationContext();
  const { getBrandConfigFromBrandId } = useBrandGroupConfig();
  const { addressServiceLanguage } = useAddressService();
  const brandConfig = useMemo(
    () => getBrandConfigFromBrandId(registrationFlowConfig.brand),
    [registrationFlowConfig.brand, getBrandConfigFromBrandId]
  );
  // Use query params to get the brand instead of registrationFlowConfig for tracking purposes. Otherwise multiple events are generated.
  const { brand } = useQueryParams();
  const { redirect } = useProgressiveRegistrationRedirect();

  const [validateOnChange, setValidateOnChange] = useState<boolean>(false);

  const dispatch = useAppDispatch();
  const { trackEvent, trackEventWithCategory } = useTracking();
  const { initialFormValues } = useInitialFormValues(
    user?.sub,
    brandConfig?.countryISO,
    registrationFlowConfig
  );

  const validationProperties = useSelector((state: RootState) => state.validationProperties);
  const getCountryConfig = useGetCountryConfig();
  const [countryConfig, setCountryConfig] = useState<CountryConfig>(
    getCountryConfig(brandConfig?.countryISO || 'default')
  );
  const onCountryChange = (iso: string) => {
    setCountryConfig(getCountryConfig(iso));
  };

  useEffect(() => {
    // On page load and brand present
    if (brand) {
      const eventCategory = getEventCategory(registrationFlowConfig.action);
      dispatch(setContext({ eventCategory, view: VIEWS.PERSONAL_INFO }));
      trackEventWithCategory(EventActionValues.Show, EventLabelValues.PersonalInfo, eventCategory);
    }
    // Only depending on brand because otherwise the useEffect would be triggered multiple times
    // and incomplete events would be tracked
    // Also get the brand from query params instead of registrationFlowConfig for the same reason
  }, [brand, dispatch, registrationFlowConfig.action]);

  useEffect(() => {
    if (showEmailVerification) {
      dispatch(setContext({ view: VIEWS.CONFIRM_EMAIL }));
      trackEvent(EventActionValues.Show, EventLabelValues.ConfirmEmail);
    }
    // Only depending on showEmailVerification because otherwise the useEffect would be triggered multiple times
    // and incomplete events would be tracked
  }, [dispatch, showEmailVerification]);

  const handleFormSubmit = async (values: RegistrationFlowFormValues) => {
    const accessToken = await window.auth.getAccessToken();
    if (!registrationFlowConfig.brand) {
      handleError(t('register.error.missingConfiguration'));
      return;
    }
    if (!user?.sub || !user?.email || !accessToken) {
      handleError(t('register.error.missingAuthentication'));
      return;
    }

    await registrationCompletionService
      .completeRegistrationFlow(user.sub, accessToken, values, orchestration)
      .catch((error) => handleError(error));
    const optinStates: OptInState[] = values.optins.map((optin) => ({
      active: optin.checked,
      code: optin.optinKey,
    }));

    const ciamToken = await getAccessToken();

    try {
      await registrationCompletionService.sendUserPreferences(
        user.sub,
        user.email,
        registrationFlowConfig.brand,
        ciamToken,
        optinStates
      );
    } catch (e) {
      console.warn(e);
      // ignore error because user preferences dependency may not block registration flow
    }

    trackEvent(EventActionValues.Click, EventLabelValues.PersonalInfoContinue);

    if (registrationFlowConfig.skipEmailVerification) {
      await redirect();
    } else {
      setShowEmailVerification(true);
    }
  };

  const handleEmailVerificationSubmit = async () => {
    trackEvent(EventActionValues.Click, EventLabelValues.ConfirmEmailContinue);
    await redirect();
  };

  const orchestration = useRegistrationFlowOrchestration();

  return !initialFormValues ? (
    <Stack justifyContent="center">
      <Loader />
    </Stack>
  ) : (
    <>
      {!showEmailVerification && (
        <RegistrationFlowForm
          orchestration={orchestration}
          onSubmit={handleFormSubmit}
          validateOnChange={validateOnChange}
          onSubmitClick={() => {
            setValidateOnChange(true);
          }}
          initialFormValues={initialFormValues}
          countryConfig={countryConfig}
          onCountryChange={onCountryChange}
          addressServiceLanguage={addressServiceLanguage}
          validationProperties={validationProperties}
        />
      )}
      {showEmailVerification && (
        <EmailVerification
          email={user?.email ?? ''}
          brandUrlDisplay={brandConfig?.urlDisplay ?? ''}
          onSubmit={handleEmailVerificationSubmit}
        />
      )}
    </>
  );
};
